import * as React from "react"
import type { FormEvent } from "react"
import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { SettingsLayout } from "@/components/settings/SettingsLayout"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { SubsequenceRulesEditor, type SubsequenceRule } from "@/components/clients/SubsequenceRulesEditor"
import type { PageProps } from "@/types/inertia"

type ClientFormRecord = {
  readonly id: number | null
  readonly display_name: string
  readonly slug: string
  readonly smartlead_campaign_keywords: readonly string[]
  readonly draft_prompt_template: string
  readonly brand_voice: string
  readonly signature_html: string
  readonly knowledge_base_md: string
  readonly calendly_url: string
  readonly sender_default_email: string
  readonly enabled: boolean
  readonly color_hex: string
  readonly subsequence_rules: readonly SubsequenceRule[]
}

type ClientFormProps = {
  readonly title: string
  readonly client: ClientFormRecord
  readonly classifier_labels: readonly string[]
}

type ClientFormData = {
  client: {
    display_name: string
    slug: string
    smartlead_campaign_keywords: string[]
    draft_prompt_template: string
    brand_voice: string
    signature_html: string
    knowledge_base_md: string
    calendly_url: string
    sender_default_email: string
    enabled: boolean
    color_hex: string
  }
}

export default function ClientForm({ title, client, classifier_labels }: ClientFormProps) {
  const { props } = usePage<PageProps<ClientFormProps>>()
  const errors = props.errors
  const description =
    "Create or edit a ReplyOps client configuration for campaign mapping, drafting context, and sender defaults."
  const form = useForm<ClientFormData>({
    client: {
      display_name: client.display_name,
      slug: client.slug,
      smartlead_campaign_keywords: [...client.smartlead_campaign_keywords],
      draft_prompt_template: client.draft_prompt_template,
      brand_voice: client.brand_voice,
      signature_html: client.signature_html,
      knowledge_base_md: client.knowledge_base_md,
      calendly_url: client.calendly_url,
      sender_default_email: client.sender_default_email,
      enabled: client.enabled,
      color_hex: client.color_hex,
    },
  })
  const [keywords, setKeywords] = useStringList(
    client.smartlead_campaign_keywords,
  )

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const payload = {
      ...form.data.client,
      smartlead_campaign_keywords: splitKeywords(keywords),
    }
    form.setData("client", payload)
    const options = { preserveScroll: true }
    if (client.id) {
      router.patch(`/settings/clients/${client.id}`, { client: payload }, options)
    } else {
      router.post("/settings/clients", { client: payload }, options)
    }
  }

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <SettingsLayout wide>
        <div className="flex flex-col gap-6">
          <header>
            <h1>{title}</h1>
            <p className="mt-2">Client configuration for matching and drafting.</p>
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}

          <form onSubmit={submit} className="space-y-6">
            <section className="rounded-lg border border-hairline bg-page p-6">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field error={errors.display_name}>
                  <label htmlFor="display_name">Display name</label>
                  <Input
                    id="display_name"
                    value={form.data.client.display_name}
                    onChange={(event) =>
                      form.setData("client", {
                        ...form.data.client,
                        display_name: event.target.value,
                      })
                    }
                  />
                </Field>
                <Field error={errors.slug}>
                  <label htmlFor="slug">Slug</label>
                  <Input
                    id="slug"
                    value={form.data.client.slug}
                    onChange={(event) =>
                      form.setData("client", {
                        ...form.data.client,
                        slug: event.target.value,
                      })
                    }
                  />
                </Field>
                <Field error={errors.color_hex}>
                  <label htmlFor="color_hex">Color</label>
                  <Input
                    id="color_hex"
                    type="color"
                    value={form.data.client.color_hex}
                    onChange={(event) =>
                      form.setData("client", {
                        ...form.data.client,
                        color_hex: event.target.value,
                      })
                    }
                  />
                </Field>
                <Field error={errors.sender_default_email}>
                  <label htmlFor="sender_default_email">Sender email</label>
                  <Input
                    id="sender_default_email"
                    type="email"
                    value={form.data.client.sender_default_email}
                    onChange={(event) =>
                      form.setData("client", {
                        ...form.data.client,
                        sender_default_email: event.target.value,
                      })
                    }
                  />
                </Field>
                <Field error={errors.smartlead_campaign_keywords}>
                  <label htmlFor="smartlead_campaign_keywords">
                    Campaign keywords
                  </label>
                  <Input
                    id="smartlead_campaign_keywords"
                    value={keywords}
                    onChange={(event) => setKeywords(event.target.value)}
                  />
                </Field>
                <Field error={errors.calendly_url}>
                  <label htmlFor="calendly_url">Calendly URL</label>
                  <Input
                    id="calendly_url"
                    value={form.data.client.calendly_url}
                    onChange={(event) =>
                      form.setData("client", {
                        ...form.data.client,
                        calendly_url: event.target.value,
                      })
                    }
                  />
                </Field>
              </div>
              <label
                htmlFor="enabled"
                className="mt-5 flex items-center gap-3 font-normal text-ink-body"
              >
                <Checkbox
                  id="enabled"
                  checked={form.data.client.enabled}
                  onChange={(event) =>
                    form.setData("client", {
                      ...form.data.client,
                      enabled: event.target.checked,
                    })
                  }
                />
                <span>Enabled</span>
              </label>
            </section>

            <section className="grid gap-5 lg:grid-cols-2">
              <TextAreaField
                id="draft_prompt_template"
                label="Draft prompt template"
                value={form.data.client.draft_prompt_template}
                error={errors.draft_prompt_template}
                onChange={(value) =>
                  form.setData("client", {
                    ...form.data.client,
                    draft_prompt_template: value,
                  })
                }
              />
              <TextAreaField
                id="brand_voice"
                label="Brand voice"
                value={form.data.client.brand_voice}
                error={errors.brand_voice}
                onChange={(value) =>
                  form.setData("client", {
                    ...form.data.client,
                    brand_voice: value,
                  })
                }
              />
              <TextAreaField
                id="signature_html"
                label="Signature HTML"
                value={form.data.client.signature_html}
                error={errors.signature_html}
                onChange={(value) =>
                  form.setData("client", {
                    ...form.data.client,
                    signature_html: value,
                  })
                }
              />
              <TextAreaField
                id="knowledge_base_md"
                label="Knowledge base"
                value={form.data.client.knowledge_base_md}
                error={errors.knowledge_base_md}
                onChange={(value) =>
                  form.setData("client", {
                    ...form.data.client,
                    knowledge_base_md: value,
                  })
                }
              />
            </section>

            <div className="flex justify-end gap-3">
              <Button asChild variant="secondary">
                <Link href="/settings/clients">Cancel</Link>
              </Button>
              <Button type="submit" disabled={form.processing}>
                Save client
              </Button>
            </div>
          </form>

          {client.id && (
            <SubsequenceRulesEditor
              clientId={client.id}
              rules={client.subsequence_rules}
              labels={classifier_labels}
            />
          )}
        </div>
      </SettingsLayout>
    </>
  )
}


function useStringList(values: readonly string[]) {
  return React.useState(values.join(", "))
}

function splitKeywords(value: string) {
  return value
    .split(/[,\s]+/)
    .map((keyword) => keyword.trim().toLowerCase())
    .filter(Boolean)
}

function Field({
  children,
  error,
}: {
  readonly children: React.ReactNode
  readonly error?: string
}) {
  return (
    <div className="space-y-2">
      {children}
      {error && <div className="text-sm text-danger-display">{error}</div>}
    </div>
  )
}

function TextAreaField({
  id,
  label,
  value,
  error,
  onChange,
}: {
  readonly id: string
  readonly label: string
  readonly value: string
  readonly error?: string
  readonly onChange: (value: string) => void
}) {
  return (
    <Field error={error}>
      <label htmlFor={id}>{label}</label>
      <textarea
        id={id}
        className="form-control form-control-textarea min-h-40"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  )
}
